import {
  Directive,
  ElementRef,
  OnDestroy,
  inject,
  afterNextRender,
} from '@angular/core';

@Directive({
  selector: '[appBootstrapInit]',
  standalone: true,
})
export class BootstrapInitDirective implements OnDestroy {
  private readonly host = inject(ElementRef) as ElementRef<HTMLElement>;
  private instances = new Map<HTMLElement, BootstrapInstance>();
  private observer: MutationObserver | null = null;

  constructor() {
    // run init and start observer, but avoid returning Promises from callbacks
    afterNextRender(() => {
      void this.initAll();

      // Setup observer for dynamic content (*ngIf, *ngFor)
      this.observer = new MutationObserver(() => {
        void this.initAll();
      });
      this.observer.observe(this.host.nativeElement as Node, {
        childList: true,
        subtree: true,
      });
    });
  }

  private async initAll() {
    const root = this.host.nativeElement;

    // Helper to avoid double-initialization
    const getNewElements = (selector: string): HTMLElement[] => {
      const nodeList = root.querySelectorAll<HTMLElement>(selector);
      return Array.from(nodeList).filter(
        (el): el is HTMLElement => !this.instances.has(el),
      );
    };

    // Tooltips
    const tooltips = getNewElements('[data-bs-toggle="tooltip"]');
    if (tooltips.length > 0) {
      const { default: Tooltip } = await import('bootstrap/js/dist/tooltip');
      tooltips.forEach((el) => this.instances.set(el, new Tooltip(el)));
    }

    // Modals
    const modals = getNewElements('.modal');
    if (modals.length > 0) {
      const { default: Modal } = await import('bootstrap/js/dist/modal');
      modals.forEach((el) =>
        this.instances.set(el, Modal.getOrCreateInstance(el)),
      );
    }

    // Dropdowns
    const dropdowns = getNewElements('[data-bs-toggle="dropdown"]');
    if (dropdowns.length > 0) {
      const { default: Dropdown } = await import('bootstrap/js/dist/dropdown');
      dropdowns.forEach((el) => this.instances.set(el, new Dropdown(el)));
    }

    // Popovers
    const popovers = getNewElements('[data-bs-toggle="popover"]');
    if (popovers.length > 0) {
      const { default: Popover } = await import('bootstrap/js/dist/popover');
      popovers.forEach((el) =>
        this.instances.set(el, new Popover(el, { container: 'body' })),
      );
    }

    // Offcanvas
    const offcanvas = getNewElements('.offcanvas');
    if (offcanvas.length > 0) {
      const { default: Offcanvas } = await import(
        'bootstrap/js/dist/offcanvas'
      );
      offcanvas.forEach((el) =>
        this.instances.set(el, Offcanvas.getOrCreateInstance(el)),
      );
    }

    // Collapse
    const collapses = getNewElements('.collapse');
    if (collapses.length > 0) {
      const { default: Collapse } = await import('bootstrap/js/dist/collapse');
      collapses.forEach((el) =>
        this.instances.set(
          el,
          Collapse.getOrCreateInstance(el, { toggle: false }),
        ),
      );
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.instances.forEach((inst) => inst.dispose?.());
    this.instances.clear();
  }
}
